/** @jsxImportSource @emotion/react */

import { Box, useTheme } from '@mui/material'
import type { RiskWorkspace } from '@app/types'
import {
  previewContainerStyles,
  previewMainStyles,
  previewTitleStyles,
  previewDescriptionStyles,
} from './WorkspacePreview.styles'
import { ModalLayer } from '@app/components/Molecules/LayersControl'
import { useMemo } from 'react'
import { workspaceToModalLayers } from '../utils/Workspaces.utils'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { LayersOrganizer } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/LayersOrganizer'
import { WorkspaceFilterChips } from '../WorkspaceFilterChips'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'

export const WorkspacePreview = ({
  selectedWorkspace,
}: {
  selectedWorkspace: RiskWorkspace | null
}) => {
  const theme = useTheme()
  const { layerData } = useSelector((state: RootState) => state.riskMap)
  const { liveLayers } = useLiveContext()

  const selectedLayers: ModalLayer[] = useMemo(() => {
    if (!selectedWorkspace || !layerData) return []

    return workspaceToModalLayers(selectedWorkspace, layerData, liveLayers)
  }, [selectedWorkspace, layerData, liveLayers])

  if (!selectedWorkspace) {
    return (
      <Box css={previewContainerStyles(theme)}>
        <i>
          Select a Workspace to
          <br />
          preview its contents
        </i>
      </Box>
    )
  }
  if (selectedLayers.length === 0) {
    return (
      <Box css={previewContainerStyles(theme)}>
        <i>
          This workspace is no longer
          <br />
          available. To find out more, contact
          <br />
          your Urban Intelligence Advisor.
        </i>
      </Box>
    )
  }

  return (
    <Box css={previewMainStyles(theme)}>
      <Box css={previewTitleStyles(theme, Boolean(selectedWorkspace.name))}>
        {selectedWorkspace.name || 'No name provided'}
      </Box>
      {selectedWorkspace.description && (
        <Box css={previewDescriptionStyles(theme, Boolean(selectedWorkspace.description))}>
          <span>{selectedWorkspace.description}</span>
        </Box>
      )}
      <WorkspaceFilterChips regions={selectedWorkspace.regions} />
      <Box>
        <LayersOrganizer
          isDialogPreview
          dialogProps={{
            stagedLayers: selectedLayers,
          }}
          style={{
            [theme.breakpoints.down('md')]: {
              flexDirection: 'row',
              flexWrap: 'wrap',
              '>div': { width: 'calc(50% - 4px)' },
            },
          }}
        />
      </Box>
    </Box>
  )
}
